import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import {
  Loader2, Plus, Play, StopCircle, LayoutDashboard, Coins, Map, Users,
  AlertTriangle, ScrollText, Swords, Gem, Clock, Trophy, Flag, ShoppingCart,
  PauseCircle, PlayCircle, TimerReset, Wrench, ShieldCheck, Radio, Eye,
} from 'lucide-react';
import GbEconomy from './gb/GbEconomy';
import GbMissions from './gb/GbMissions';
import GbPlayers from './gb/GbPlayers';
import GbMatches from './gb/GbMatches';
import GbPvpMatches from './gb/GbPvpMatches';
import GbAudit from './gb/GbAudit';
import GbJackpot from './gb/GbJackpot';
import GbWithdrawals from './gb/GbWithdrawals';
import GbFinalPrizes from './gb/GbFinalPrizes';
import GbCoinSales from './gb/GbCoinSales';
import { fmt, fmtDate, gbErr } from './gb/gbAdminUtils';

const SECTIONS = [
  { id: 'visao', label: 'Visão geral', icon: LayoutDashboard },
  { id: 'economia', label: 'Economia', icon: Coins },
  { id: 'fases', label: 'Fases da estrada', icon: Map },
  { id: 'jogadores', label: 'Jogadores', icon: Users },
  { id: 'suspeitos', label: 'Suspeitos', icon: AlertTriangle },
  { id: 'partidas', label: 'Partidas das fases', icon: Swords },
  { id: 'duelos', label: 'Duelos PvP', icon: Radio },
  { id: 'resgates', label: 'Resgates', icon: Coins },
  { id: 'jackpot', label: 'Jackpot PvP', icon: Trophy },
  { id: 'fechamento', label: 'Fechamento', icon: Flag },
  { id: 'vendas', label: 'Vendas no jogo', icon: ShoppingCart },
  { id: 'auditoria', label: 'Auditoria', icon: ScrollText },
];

const Stat = ({ label, value, tone = 'text-slate-700', testid }) => (
  <div className="rounded-xl border p-3" data-testid={testid}>
    <p className="text-[10px] uppercase text-slate-400">{label}</p>
    <p className={`text-base font-black ${tone}`}>{value}</p>
  </div>
);

export default function GbArenaManagement() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [section, setSection] = useState('visao');
  const [busy, setBusy] = useState(false);
  const [novo, setNovo] = useState({ name: '' });
  const [gate, setGate] = useState(null);
  const [selected, setSelected] = useState(null);   // temporada escolhida na lista (null = no ar / última encerrada)

  const load = useCallback(async (seasonId = selected) => {
    try {
      const res = await api.get('/admin/gb/overview', { params: seasonId ? { season_id: seasonId } : {} });
      setData(res.data);
    } catch (e) { toast.error(gbErr(e)); } finally { setLoading(false); }
  }, [selected]);

  // trava das combinações: enquanto bloqueado, acompanha de perto as partidas em curso
  const loadGate = useCallback(async () => {
    try { const r = await api.get('/admin/gb/pvp/gate'); setGate(r.data); }
    catch (e) { /* silencioso: o painel principal já avisa erro */ }
  }, []);

  useEffect(() => { load(); loadGate(); }, [load, loadGate]);

  useEffect(() => {
    if (!gate?.locked) return;
    const t = setInterval(loadGate, 8000);
    return () => clearInterval(t);
  }, [gate?.locked, loadGate]);

  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); await load(); }
    catch (e) { toast.error(gbErr(e)); } finally { setBusy(false); }
  };

  if (loading) {
    return <div className="flex justify-center p-10"><Loader2 className="h-6 w-6 animate-spin text-orange-500" /></div>;
  }

  const active = data?.season;          // temporada em exibição (no ar, escolhida ou a última encerrada)
  const encerrada = active?.status === 'closed';
  const flowIn = data?.flow?.in?.total || 0;
  const flowOut = Math.abs(data?.flow?.out?.total || 0);

  return (
    <div className="space-y-4" data-testid="gb-arena-management">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex flex-wrap items-center gap-2 text-base">
            🎃 GB Arena — gestão do evento
            {active ? (
              <span className={`rounded-full px-2 py-0.5 text-xs font-black ${
                encerrada ? 'bg-slate-200 text-slate-700'
                  : data?.event_status === 'desativado' ? 'bg-orange-100 text-orange-700'
                    : data?.event_status === 'encerrando' ? 'bg-sky-100 text-sky-700'
                      : data?.live ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}
                data-testid="gb-event-status">
                {active.name} · {encerrada ? `encerrada em ${fmtDate(active.closed_at)}`
                  : data?.event_status === 'parada' ? 'parada (ainda não foi ao ar)'
                    : data?.event_status === 'desativado' ? 'desativado'
                      : data?.event_status === 'encerrando' ? 'encerrando (sem partidas novas)'
                        : data?.live ? 'no ar' : 'fora do prazo'}
                {data?.live && Number(data?.seconds_left) > 0
                  && ` · faltam ${Math.ceil(Number(data.seconds_left) / 86400)} dia(s)`}
              </span>
            ) : (
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-black text-slate-500">
                nenhuma temporada no ar
              </span>
            )}
            {active && !data?.is_active && !data?.active && (
              <span className="text-[11px] font-normal text-slate-500" data-testid="gb-no-active-hint">
                (nenhuma temporada no ar — mostrando os dados desta)
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {encerrada && (
            <div className="rounded-xl border-2 border-amber-400 bg-amber-50 p-3 text-xs text-amber-900" data-testid="gb-closed-banner">
              <p className="font-black">Temporada encerrada — os dados continuam todos aqui.</p>
              <p className="mt-0.5">
                Ganhadores dos rankings e o <b>saldo de moedas amarelas que cada cliente tinha na carteira</b> estão
                congelados na aba <b>Fechamento</b>, aguardando a sua aprovação para serem creditados na KAKO.
                {Number(data?.final_pending || 0) > 0 && (
                  <> <b className="text-rose-700">{data.final_pending} pagamento(s) pendente(s).</b></>
                )}
              </p>
              <Button size="sm" className="mt-2" data-testid="gb-closed-go-final" onClick={() => setSection('fechamento')}>
                <Flag className="mr-1 h-3.5 w-3.5" /> Abrir fechamento (ganhadores e saldos)
              </Button>
            </div>
          )}
          {active && (
            <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
              <Stat label="Jogadores" value={fmt(data.players)} testid="gb-stat-players" />
              <Stat label="Partidas" value={`${fmt(data.matches)} (${fmt(data.matches_open)} abertas)`} testid="gb-stat-matches" />
              <Stat label="Diamantes distribuídos" value={`💎 ${fmt(flowIn)}`} tone="text-emerald-600" testid="gb-stat-in" />
              <Stat label="Diamantes gastos" value={`💎 ${fmt(flowOut)}`} tone="text-rose-600" testid="gb-stat-out" />
              <Stat label="Contas suspeitas" value={fmt(data.suspects)} tone={data.suspects ? 'text-amber-600' : 'text-slate-700'} testid="gb-stat-suspects" />
              <Stat label="Contas bloqueadas" value={fmt(data.blocked)} tone={data.blocked ? 'text-rose-600' : 'text-slate-700'} testid="gb-stat-blocked" />
              <Stat label="Começou" value={fmtDate(active.starts_at)} />
              <Stat label={encerrada ? 'Encerrou' : 'Termina'} value={fmtDate(encerrada ? active.closed_at : active.ends_at)} />
            </div>
          )}

          {/* MANUTENÇÃO PROGRAMADA: travar combinações antes de publicar */}
          <div className={`rounded-xl border-2 p-3 ${gate?.locked ? 'border-amber-400 bg-amber-50' : 'border-slate-200 bg-white'}`}
            data-testid="gb-pvp-gate">
            <div className="flex flex-wrap items-center gap-2">
              <p className="flex items-center gap-1.5 text-sm font-black text-slate-700">
                <Wrench className="h-4 w-4 text-amber-600" /> Manutenção programada (antes de publicar)
              </p>
              <span className={`rounded-full px-2 py-0.5 text-[11px] font-black ${
                gate?.locked ? 'bg-amber-200 text-amber-900' : 'bg-emerald-100 text-emerald-700'}`}
                data-testid="gb-pvp-gate-status">
                {gate?.locked ? 'combinações e fases BLOQUEADAS' : 'combinações e fases liberadas'}
              </span>
              {gate?.locked ? (
                <Button size="sm" disabled={busy} className="ml-auto bg-emerald-600 hover:bg-emerald-700"
                  data-testid="gb-pvp-gate-unlock"
                  onClick={() => act(async () => { await api.post('/admin/gb/pvp/gate', { locked: false }); await loadGate(); },
                    'Liberado: os jogadores já podem duelar e entrar nas fases.')}>
                  <PlayCircle className="mr-1 h-4 w-4" /> Liberar combinações e fases
                </Button>
              ) : (
                <Button size="sm" disabled={busy} className="ml-auto bg-amber-600 hover:bg-amber-700"
                  data-testid="gb-pvp-gate-lock"
                  onClick={() => act(async () => { await api.post('/admin/gb/pvp/gate', { locked: true }); await loadGate(); },
                    'Combinações e fases bloqueadas. Espere as partidas em curso acabarem para publicar.')}>
                  <PauseCircle className="mr-1 h-4 w-4" /> Bloquear combinações e fases
                </Button>
              )}
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2 md:grid-cols-5">
              <Stat label="Duelos em andamento" value={fmt(gate?.live?.matches || 0)}
                tone={gate?.live?.matches ? 'text-rose-600' : 'text-emerald-600'} testid="gb-pvp-gate-matches" />
              <Stat label="Fases em andamento" value={fmt(gate?.live?.missions || 0)}
                tone={gate?.live?.missions ? 'text-rose-600' : 'text-emerald-600'} testid="gb-pvp-gate-missions" />
              <Stat label="Na fila" value={fmt(gate?.live?.queue || 0)} testid="gb-pvp-gate-queue" />
              <Stat label="Na sala de espera" value={fmt(gate?.live?.room || 0)} />
              <Stat label="Conectados" value={fmt(gate?.live?.online || 0)} />
            </div>
            {gate?.locked && (
              <p className={`mt-2 flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-[12px] font-bold ${
                gate?.safe_to_deploy ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'}`}
                data-testid="gb-pvp-gate-safe">
                {gate?.safe_to_deploy
                  ? <><ShieldCheck className="h-4 w-4" /> Nenhuma partida em andamento — pode publicar agora.</>
                  : <><Clock className="h-4 w-4" /> {fmt(gate?.running)} partida(s) ainda em andamento — espere terminar antes de publicar.</>}
              </p>
            )}
            <p className="mt-2 text-[11px] text-slate-500">
              Bloqueia tudo que COMEÇA partida: duelo novo de PvP e entrada nas fases da campanha.
              Quem já está jogando termina a partida e recebe o prêmio normalmente. Os jogadores veem
              o aviso: <b>“{gate?.message || 'Manutenção programada: as combinações e as fases voltam em breve.'}”</b>
            </p>
          </div>

          <div className="flex flex-wrap items-end gap-2 rounded-xl border bg-slate-50 p-3">
            <label className="min-w-[200px] flex-1 text-xs font-bold text-slate-600">
              <span className="block">Nova temporada</span>
              <input value={novo.name} onChange={(e) => setNovo({ ...novo, name: e.target.value })}
                data-testid="gb-season-name" placeholder="Ex.: Arena Halloween"
                className="mt-1 h-9 w-full rounded-lg border px-2 text-sm" />
            </label>
            <div className="text-xs font-bold text-slate-600" data-testid="gb-season-days">
              <span className="block">Duração</span>
              <span className="mt-1 flex h-9 items-center rounded-lg border bg-white px-3 text-sm text-slate-500">
                30 dias (fixo)
              </span>
            </div>
            <Button size="sm" disabled={busy || novo.name.trim().length < 3} data-testid="gb-season-create"
              className="bg-orange-600 text-white hover:bg-orange-700"
              onClick={() => act(() => api.post('/admin/gb/season',
                { name: novo.name.trim(), days: 30 }), 'Temporada criada (ainda não está no ar).')}>
              <Plus className="mr-1 h-4 w-4" /> Criar
            </Button>
            <p className="w-full text-[11px] text-slate-500">
              A temporada nasce parada com os números padrão. Ativar coloca no ar, encerra a anterior e
              começa a contar os 30 dias. No fim do prazo o evento se encerra sozinho: ninguém começa
              partida nova e quem está jogando termina a dele em paz.
            </p>
          </div>

          <div className="space-y-1">
            {(data?.seasons || []).map((s) => (
              <div key={s.season_id}
                className={`flex flex-wrap items-center gap-2 rounded-lg border p-2 text-xs ${active?.season_id === s.season_id ? 'border-orange-400 bg-orange-50/60' : ''}`}
                data-testid={`gb-season-${s.season_id}`} data-selected={active?.season_id === s.season_id ? '1' : undefined}>
                <b className="text-slate-700">{s.name}</b>
                <span className={`rounded-full px-2 py-0.5 font-bold ${
                  s.status === 'active' && !s.paused ? 'bg-emerald-100 text-emerald-700'
                    : s.paused ? 'bg-orange-100 text-orange-700'
                      : s.status === 'finalizing' ? 'bg-sky-100 text-sky-700'
                        : s.status === 'draft' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'}`}
                  data-testid={`gb-season-badge-${s.season_id}`}>
                  {s.paused ? 'desativado' : s.status === 'active' ? 'no ar'
                    : s.status === 'finalizing' ? 'encerrando' : s.status === 'draft' ? 'parada' : 'encerrada'}
                </span>
                <span className="flex items-center gap-1 text-slate-400">
                  <Clock className="h-3 w-3" /> {s.days} dias · {fmtDate(s.starts_at)} → {fmtDate(s.ends_at)}
                </span>
                <span className="ml-auto flex gap-1">
                  {active?.season_id !== s.season_id && (
                    <Button size="sm" variant="outline" disabled={busy} data-testid={`gb-view-${s.season_id}`}
                      onClick={() => { setSelected(s.season_id); setSection(s.status === 'closed' ? 'fechamento' : 'visao'); load(s.season_id); }}>
                      <Eye className="mr-1 h-3.5 w-3.5" /> Ver dados
                    </Button>
                  )}
                  {s.status === 'draft' && (
                    <Button size="sm" variant="outline" disabled={busy} data-testid={`gb-activate-${s.season_id}`}
                      onClick={() => {
                        if (!window.confirm(`Colocar "${s.name}" no ar? A temporada atual será encerrada.`)) return;
                        act(() => api.post(`/admin/gb/season/${s.season_id}/activate`), 'Temporada no ar!');
                      }}>
                      <Play className="mr-1 h-3.5 w-3.5" /> Ativar
                    </Button>
                  )}
                  {s.status === 'active' && !s.paused && (
                    <Button size="sm" variant="outline" disabled={busy} data-testid={`gb-pause-${s.season_id}`}
                      className="text-orange-700"
                      onClick={() => {
                        const r = window.prompt('Desativar o evento. Motivo (fica na auditoria):', 'manutenção');
                        if (!r || r.trim().length < 3) return;
                        act(() => api.post(`/admin/gb/season/${s.season_id}/pause`, { reason: r.trim() }),
                          'Evento desativado — fora do ar para os jogadores.');
                      }}>
                      <PauseCircle className="mr-1 h-3.5 w-3.5" /> Desativar
                    </Button>
                  )}
                  {s.status === 'active' && s.paused && (
                    <Button size="sm" variant="outline" disabled={busy} data-testid={`gb-resume-${s.season_id}`}
                      className="text-emerald-700"
                      onClick={() => {
                        const r = window.prompt('Reativar o evento. Motivo (fica na auditoria):', 'fim da manutenção');
                        if (!r || r.trim().length < 3) return;
                        act(() => api.post(`/admin/gb/season/${s.season_id}/resume`, { reason: r.trim() }),
                          'Evento de volta ao ar — o prazo foi esticado pelo tempo parado.');
                      }}>
                      <PlayCircle className="mr-1 h-3.5 w-3.5" /> Reativar
                    </Button>
                  )}
                  {(s.status === 'active' || s.status === 'finalizing') && (
                    <Button size="sm" variant="outline" disabled={busy} data-testid={`gb-finish-${s.season_id}`}
                      onClick={() => {
                        if (!window.confirm(`Encerrar "${s.name}"?\n\nNinguém começa partida nova; quem está jogando termina a dele. Os prêmios são congelados quando a última partida acabar.`)) return;
                        const r = window.prompt('Motivo do encerramento (fica na auditoria):', 'fim do evento');
                        if (!r || r.trim().length < 3) return;
                        act(() => api.post(`/admin/gb/season/${s.season_id}/finish`, { reason: r.trim() }),
                          'Evento encerrando: sem partidas novas, as em curso terminam.');
                      }}>
                      <TimerReset className="mr-1 h-3.5 w-3.5" /> Encerrar
                    </Button>
                  )}
                  {(s.status === 'closed' || s.status === 'finalizing') && (
                    <Button size="sm" variant="outline" disabled={busy} data-testid={`gb-reopen-${s.season_id}`}
                      className="text-emerald-700"
                      onClick={() => {
                        if (!window.confirm(`Reativar "${s.name}"?\n\nA MESMA temporada volta ao ar com jogadores, progresso e carteiras. O saldo devolvido que ainda não foi creditado volta para a carteira dos clientes; prêmios ainda não pagos são descartados (o próximo fechamento recalcula). O prazo é esticado pelo tempo que ficou fechada.`)) return;
                        const r = window.prompt('Motivo da reativação (fica na auditoria):', 'encerrada por engano');
                        if (!r || r.trim().length < 3) return;
                        act(() => api.post(`/admin/gb/season/${s.season_id}/reopen`, { reason: r.trim() }),
                          'Temporada de volta ao ar — mesmos jogadores, progresso e carteiras.');
                      }}>
                      <PlayCircle className="mr-1 h-3.5 w-3.5" /> Reativar
                    </Button>
                  )}
                  {s.status === 'finalizing' && (
                    <Button size="sm" variant="outline" disabled={busy} data-testid={`gb-close-${s.season_id}`}
                      className="text-rose-700"
                      onClick={() => {
                        if (!window.confirm(`Fechar "${s.name}" AGORA? Quem estiver jogando perde a partida em andamento e os prêmios são congelados na hora.`)) return;
                        act(() => api.post(`/admin/gb/season/${s.season_id}/close`), 'Temporada fechada e prêmios congelados.');
                      }}>
                      <StopCircle className="mr-1 h-3.5 w-3.5" /> Fechar agora
                    </Button>
                  )}
                </span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {active && (
        <Card data-testid="gb-admin-panel">
          <CardHeader className="pb-2">
            <div className="flex flex-wrap gap-1">
              {SECTIONS.map((s) => {
                const Icon = s.icon;
                return (
                  <button key={s.id} onClick={() => setSection(s.id)} data-testid={`gb-admin-tab-${s.id}`}
                    className={`inline-flex items-center gap-1 rounded-lg border px-3 py-1.5 text-xs font-bold transition-colors ${
                      section === s.id ? 'border-orange-400 bg-orange-50 text-orange-700' : 'text-slate-500 hover:bg-slate-50'}`}>
                    {s.id === 'resgates' && Number(data?.withdrawals_stuck || 0) > 0 && (
                      <span className="ml-1 rounded-full bg-red-600 px-1.5 text-[10px] font-black text-white"
                        data-testid="gb-menu-withdrawals-alert">{data.withdrawals_stuck}</span>
                    )}
                    {s.id === 'fechamento' && Number(data?.final_pending || 0) > 0 && (
                      <span className="ml-1 rounded-full bg-amber-600 px-1.5 text-[10px] font-black text-white"
                        data-testid="gb-menu-final-alert">{data.final_pending}</span>
                    )}
                    {s.id === 'vendas' && Number(data?.coin_sales_review || 0) > 0 && (
                      <span className="ml-1 rounded-full bg-red-600 px-1.5 text-[10px] font-black text-white"
                        data-testid="gb-menu-sales-alert">{data.coin_sales_review}</span>
                    )}
                    <Icon className="h-3.5 w-3.5" /> {s.label}
                  </button>
                );
              })}
            </div>
          </CardHeader>
          <CardContent>
            {section === 'visao' && (
              <div className="space-y-3">
                <div className="grid gap-2 md:grid-cols-3">
                  <Stat label="Diamantes por compra" tone="text-cyan-700"
                    value={`${((active.config?.economy?.diamond_rate || 0) * 100).toLocaleString('pt-BR', { maximumFractionDigits: 3 })}%`} />
                  <Stat label="Prêmio máximo das fases" tone="text-orange-600"
                    value={`💎 ${fmt((active.config?.missions?.nodes || []).reduce((s, n) => s + Number(n.reward_total || n.reward || 0), 0))}`} />
                  <Stat label="Fases na estrada" value={fmt((active.config?.missions?.nodes || []).length)} />
                </div>
                <p className="rounded-lg border border-amber-300 bg-amber-50 p-2 text-xs font-bold text-amber-800">
                  <Gem className="mr-1 inline h-3.5 w-3.5" />
                  Contra o oponente do sistema, quem informa a vitória é o navegador do jogador. A trava
                  real é o prêmio máximo de cada fase (pago uma única vez, por estrelas) e as marcações
                  de partida suspeita. O PvP, que paga moedas KAKO, vai rodar no servidor.
                </p>
                <GbMatches seasonId={active.season_id} />
              </div>
            )}
            {section === 'economia' && (
              <GbEconomy seasonId={active.season_id} config={active.config} onSaved={load} />
            )}
            {section === 'fases' && (
              <GbMissions seasonId={active.season_id} config={active.config} onSaved={load} />
            )}
            {section === 'jogadores' && <GbPlayers seasonId={active.season_id} onChanged={load} />}
            {section === 'suspeitos' && (
              <div className="space-y-4">
                <GbPlayers seasonId={active.season_id} suspectsOnly onChanged={load} />
                <GbMatches seasonId={active.season_id} flaggedOnly />
              </div>
            )}
            {section === 'partidas' && <GbMatches seasonId={active.season_id} />}
            {section === 'duelos' && <GbPvpMatches seasonId={active.season_id} />}
            {section === 'resgates' && <GbWithdrawals />}
            {section === 'jackpot' && <GbJackpot seasonId={active.season_id} seasonStatus={active.status} />}
            {section === 'fechamento' && <GbFinalPrizes seasonId={active.season_id} seasonStatus={active.status} />}
            {section === 'vendas' && <GbCoinSales />}
            {section === 'auditoria' && <GbAudit seasonId={active.season_id} />}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
